import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {
  Icon,
  PageFrame,
  PageHeader,
  Section,
  WS_COLOR,
  WS_RADIUS,
  WS_STATUS,
  WorkspaceThemeProvider,
  icons,
  type IconProps,
  type WsTone,
} from '@platform/ui-kit/workspace';

/**
 * Help & Support — AI Readiness, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `HelpSupport.tsx`, which stays the
 * default until the owner approves this one (CLAUDE.md 1.2).
 *
 * WHAT IS UNCHANGED: the two contacts (photo, role, name, email, phone, with the initials shown
 * if a photo fails), the four department cards and all sixteen bullets, the "Need Additional
 * Help?" call to action and its address, and every word.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header, the page is
 * the one scroll region), flat sections in place of stacked cards, Lucide icons in place of
 * Material Icons and inline SVG, each department's colour as a status pair, and the support link
 * as the kit's button.
 */

interface Contact {
  photo: string;
  initials: string;
  label: string;
  name: string;
  email: string;
  phoneHref: string;
  phoneText: string;
  primary?: boolean;
}

/* Copied from HelpSupport.tsx: the original's data is not exported. */
const CONTACTS: Contact[] = [
  {
    photo: '/images/Goran_Lucic.png',
    initials: 'GL',
    label: 'Primary Contact',
    name: 'Goran Lucic',
    email: 'Goran.Lucic@talogent.com',
    phoneHref: '+14045470589',
    phoneText: '+1.404.547.0589',
    primary: true,
  },
  {
    photo: '/images/Joe_Schuller.jpeg',
    initials: 'JS',
    label: 'Contact',
    name: 'Joe Schuller',
    email: 'Joe.Schuller@talogent.com',
    phoneHref: '+16784785014',
    phoneText: '+1.678.478.5014',
  },
];

interface Department {
  tone: Exclude<WsTone, 'error'>;
  icon: IconProps['icon'];
  title: string;
  desc: string;
  features: string[];
}

const DEPARTMENTS: Department[] = [
  {
    tone: 'info',
    icon: icons.MessageCircle,
    title: 'Technical Support',
    desc: 'Get help with technical issues, bugs, and system problems',
    features: [
      'Bug Reports & Issue Resolution',
      'System Configuration Assistance',
      'Performance Troubleshooting',
      'Integration Support',
    ],
  },
  {
    tone: 'success',
    icon: icons.DollarSign,
    title: 'Sales',
    desc: 'Inquiries about licensing, pricing, and enterprise solutions',
    features: [
      'Enterprise Licensing Options',
      'Custom Pricing Packages',
      'Volume Discounts',
      'Subscription Management',
    ],
  },
  {
    tone: 'warning',
    icon: icons.Star,
    title: 'Marketing',
    desc: 'Partnership opportunities, media inquiries, and promotions',
    features: [
      'Strategic Partnerships',
      'Media & Press Inquiries',
      'Case Studies & Success Stories',
      'Co-Marketing Opportunities',
    ],
  },
  {
    tone: 'neutral',
    icon: icons.GraduationCap,
    title: 'Training & Enablement',
    desc: 'Comprehensive training programs for success',
    features: [
      'Platform Training Sessions',
      'Best Practices Workshops',
      'Custom Documentation',
      'Team Onboarding Programs',
    ],
  },
];

export function HelpSupportWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  return (
    <PageFrame
      header={
        <PageHeader
          title="Help & Support"
          subtitle="Get assistance with the AI Readiness platform - we're here to help"
        />
      }
    >
      <Box tabIndex={0} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pb: 6 }}>
        <Box sx={{ maxWidth: 960 }}>
          <Section title="Contact Information">
            <Box
              sx={{
                display: 'grid',
                gap: 4,
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              }}
            >
              {CONTACTS.map((c) => (
                <Box
                  key={c.name}
                  sx={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                    p: 4,
                    border: `${c.primary ? 2 : 1}px solid ${c.primary ? WS_COLOR.primary : WS_COLOR.border}`,
                    borderRadius: `${WS_RADIUS.card}px`,
                  }}
                >
                  {/* The initials show if the photo fails: Avatar does that on its own. */}
                  <Avatar
                    src={c.photo}
                    alt={c.name}
                    sx={{
                      width: 72,
                      height: 72,
                      fontSize: 24,
                      flex: 'none',
                      backgroundColor: WS_STATUS.info.fill,
                      color: WS_STATUS.info.ink,
                    }}
                  >
                    {c.initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
                      {c.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>{c.name}</Typography>
                    <Link
                      href={`mailto:${c.email}`}
                      sx={{ display: 'block', overflowWrap: 'anywhere' }}
                    >
                      {c.email}
                    </Link>
                    <Link href={`tel:${c.phoneHref}`} sx={{ display: 'block' }}>
                      {c.phoneText}
                    </Link>
                  </Box>
                </Box>
              ))}
            </Box>
          </Section>

          <Section title="Department Contacts">
            <Box
              sx={{
                display: 'grid',
                gap: 4,
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              }}
            >
              {DEPARTMENTS.map((d) => (
                <Box
                  key={d.title}
                  sx={{
                    p: 4,
                    border: `1px solid ${WS_COLOR.border}`,
                    borderRadius: `${WS_RADIUS.card}px`,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box
                      aria-hidden
                      sx={{
                        flex: 'none',
                        width: 40,
                        height: 40,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: `${WS_RADIUS.field}px`,
                        backgroundColor: WS_STATUS[d.tone].fill,
                        color: WS_STATUS[d.tone].ink,
                      }}
                    >
                      <Icon icon={d.icon} />
                    </Box>
                    <Box>
                      <Typography variant="h3" component="h3">
                        {d.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                        {d.desc}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="ul"
                    sx={{ listStyle: 'none', m: 0, mt: 3, p: 0, display: 'grid', gap: 1 }}
                  >
                    {d.features.map((f) => (
                      <Box
                        component="li"
                        key={f}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          color: WS_STATUS[d.tone].ink,
                        }}
                      >
                        <Icon icon={icons.CheckCircle2} size={16} />
                        <Typography variant="body2" sx={{ color: WS_COLOR.text }}>
                          {f}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Section>

          <Section
            title="Need Additional Help?"
            description="Our support team is ready to assist you with any questions."
          >
            <Button
              variant="contained"
              component="a"
              href="mailto:support@talogent.com"
              startIcon={<Icon icon={icons.Mail} size={16} />}
            >
              Contact Support
            </Button>
          </Section>
        </Box>
      </Box>
    </PageFrame>
  );
}
